import { Reference, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Form from '@components/forms/Form';
import {
  FolderCreateDocument,
  FolderCreateMutation,
  FolderCreateMutationVariables,
  FolderFragment,
  FolderFragmentDoc,
  FolderUpdateDocument,
  FolderUpdateMutation,
  FolderUpdateMutationVariables,
  GetJourneyWithDocumentsQuery
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  title: string;
};

type Props = {
  autofocus: boolean;
  folder?: FolderFragment | null;
  journey: GetJourneyWithDocumentsQuery['journey'];
  onCancel: () => void;
  onSuccess: () => void;
};

const FolderForm = ({ autofocus, folder, journey, onCancel, onSuccess }: Props) => {
  const intl = useIntl();
  const defaultValues: FormData = {
    title: folder?.title
      ? folder.title
      : folder?.kind
        ? intl.formatMessage({ id: `models.folderKind.${folder.kind}` })
        : ''
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createFolder] = useMutation<FolderCreateMutation, FolderCreateMutationVariables>(FolderCreateDocument, {
    update: (store, { data }) => {
      if (data?.folderCreate?.folder) {
        store.modify({
          fields: {
            folders(cachedFolderRefs: readonly Reference[]) {
              const newFolderRef = store.writeFragment({
                data: data.folderCreate?.folder,
                fragment: FolderFragmentDoc,
                fragmentName: 'Folder'
              });
              if (newFolderRef && data.folderCreate?.folder) {
                return [...cachedFolderRefs, newFolderRef];
              }
              return [...cachedFolderRefs];
            }
          },
          id: store.identify({ ...journey })
        });
      }
    }
  });
  const [updateFolder] = useMutation<FolderUpdateMutation, FolderUpdateMutationVariables>(FolderUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      title: yup.string().required(intl.formatMessage({ id: 'errors.folder.title.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if (folder?.id) {
      return updateFolder({
        variables: {
          ...form.getValues(),
          id: folder.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.folderUpdate,
          intl,
          onSuccess,
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.folder'
        });
      });
    }
    return createFolder({
      variables: {
        ...form.getValues(),
        journeyId: journey.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.folderCreate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.folder'
      });
    });
  }, [createFolder, folder?.id, form, intl, journey.id, onSuccess, updateFolder]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input
        autofocus={autofocus}
        control={form.control}
        error={form.formState.errors.title}
        label={intl.formatMessage({ id: 'forms.folder.titleLabel' })}
        name="title"
        required
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveKey={folder ? undefined : 'forms.folder.create'} />
    </Form>
  );
};

export default FolderForm;
